import {
	CloseOutlined,
	InboxOutlined,
	PaperClipOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Checkbox,
	Drawer,
	Form,
	Input,
	Popconfirm,
	Result,
	Select,
	Upload,
} from "antd";
import type { RcFile } from "antd/es/upload";
import { isAxiosError } from "axios";
import { useContext, useId, useState } from "react";
import { HomeContext } from "../../containers/Home";
import {
	HelpRequestResult,
	submitHelpRequest,
} from "../../services/api-server/help_centre";
import { useUserStore } from "../../stores/userStore";
import fileTools from "../../utils/fileTools";
import {
	checkAttachments,
	MAX_ATTACHMENTS,
	MAX_ATTACHMENTS_MB,
	MAX_MESSAGE_LENGTH,
	MAX_SUBJECT_LENGTH,
	profilePhone,
	totalBytes,
} from "./contactRules";

const CONSENT_TEXT =
	"I consent to Elemental Energies storing my submitted information so they can respond to my request.";

type ContactMethod = "email" | "phone";

/** What the server said went wrong, or a plain account of it. */
const sendErrorMessage = (error: unknown): string => {
	if (isAxiosError(error)) {
		const userMessage = error.response?.data?.error?.userMessage;
		if (typeof userMessage === "string" && userMessage) return userMessage;
		if (!error.response) {
			return "We couldn't reach Terra. Check your connection and try again.";
		}
	}
	return "We couldn't send your request. Please try again.";
};

interface ContactSupportDrawerProps {
	open: boolean;
	onClose: () => void;
}

/**
 * The contact form, as Engage's: the account's details shown read-only,
 * then subject, message, attachments, preferred contact method and consent.
 * It stays mounted while the Help Centre is open, so closing it (or moving
 * between Help Centre pages) keeps the draft; Cancel discards it.
 */
function ContactSupportDrawer({
	open,
	onClose,
}: Readonly<ContactSupportDrawerProps>) {
	const { currentTenantKey } = useContext(HomeContext) as {
		currentTenantKey?: string;
	};
	const user = useUserStore((state) => state.user);
	const phone = profilePhone(user);
	const ids = useId();

	const [subject, setSubject] = useState("");
	const [message, setMessage] = useState("");
	const [files, setFiles] = useState<RcFile[]>([]);
	const [contactMethod, setContactMethod] = useState<ContactMethod>("email");
	const [consent, setConsent] = useState(false);
	const [attachmentError, setAttachmentError] = useState("");
	const [sendError, setSendError] = useState("");
	const [isSending, setSending] = useState(false);
	const [sent, setSent] = useState<HelpRequestResult | null>(null);

	const isDirty = !!(subject.trim() || message.trim() || files.length);
	const canSend =
		!!subject.trim() &&
		!!message.trim() &&
		consent &&
		!!currentTenantKey &&
		!isSending;

	const resetDraft = () => {
		setSubject("");
		setMessage("");
		setFiles([]);
		setContactMethod("email");
		setConsent(false);
		setAttachmentError("");
		setSendError("");
	};

	const discard = () => {
		resetDraft();
		onClose();
	};

	const addFiles = (added: RcFile[]) => {
		const result = checkAttachments(files, added);
		if ("error" in result) {
			setAttachmentError(result.error);
		} else {
			setAttachmentError("");
			setFiles(result.files);
		}
	};

	const send = async () => {
		if (!canSend || !currentTenantKey) return;
		setSending(true);
		setSendError("");
		try {
			const result = await submitHelpRequest(
				{
					subject,
					message,
					consent: true,
					phone,
					contactMethod,
					files,
				},
				currentTenantKey
			);
			resetDraft();
			setSent(result);
		} catch (error) {
			setSendError(sendErrorMessage(error));
		} finally {
			setSending(false);
		}
	};

	const footer = sent ? null : (
		<div className="help-contact-drawer__footer">
			{isDirty ? (
				<Popconfirm
					title="Discard this request?"
					description="What you've written and attached will be lost."
					okText="Discard"
					okType="danger"
					cancelText="Keep editing"
					onConfirm={discard}
				>
					<Button disabled={isSending}>Cancel</Button>
				</Popconfirm>
			) : (
				<Button onClick={onClose} disabled={isSending}>
					Cancel
				</Button>
			)}
			<Button
				type="primary"
				onClick={send}
				loading={isSending}
				disabled={!canSend}
			>
				Submit
			</Button>
		</div>
	);

	return (
		<Drawer
			rootClassName="help-contact-drawer"
			title="Submit a bug or system improvement"
			placement="right"
			width={800}
			maskClosable={false}
			open={open}
			onClose={onClose}
			// After a request is sent, the next open starts on the form again.
			afterOpenChange={(isOpen) => {
				if (!isOpen) setSent(null);
			}}
			footer={footer}
		>
			{sent ? (
				<Result
					status="success"
					title="Request sent"
					subTitle={
						sent.confirmationSent
							? `Your reference is ${sent.reference}. We've emailed you a confirmation.`
							: `Your reference is ${sent.reference}. We couldn't email you a confirmation, so please note it.`
					}
					extra={
						<Button type="primary" onClick={onClose}>
							Close
						</Button>
					}
				/>
			) : (
				<Form layout="vertical" className="help-contact-form">
					<div className="help-contact-form__row">
						<Form.Item label="Full name" htmlFor={`${ids}-name`}>
							<Input id={`${ids}-name`} value={user?.name ?? ""} readOnly />
						</Form.Item>
						<Form.Item label="Email" htmlFor={`${ids}-email`}>
							<Input id={`${ids}-email`} value={user?.email ?? ""} readOnly />
						</Form.Item>
					</div>
					<Form.Item
						label="Phone"
						htmlFor={`${ids}-phone`}
						extra="From your profile. Change it in My profile."
					>
						<Input
							id={`${ids}-phone`}
							value={phone ?? ""}
							placeholder="Not in your profile"
							readOnly
						/>
					</Form.Item>
					<Form.Item label="Subject" htmlFor={`${ids}-subject`} required>
						<Input
							id={`${ids}-subject`}
							value={subject}
							onChange={(event) => setSubject(event.target.value)}
							maxLength={MAX_SUBJECT_LENGTH}
							showCount
						/>
					</Form.Item>
					<Form.Item label="Message" htmlFor={`${ids}-message`} required>
						<Input.TextArea
							id={`${ids}-message`}
							value={message}
							onChange={(event) => setMessage(event.target.value)}
							maxLength={MAX_MESSAGE_LENGTH}
							showCount
							autoSize={{ minRows: 5, maxRows: 12 }}
						/>
					</Form.Item>
					<Form.Item label="Attachments">
						<Upload.Dragger
							multiple
							showUploadList={false}
							disabled={isSending}
							// The files are sent with the request, so nothing uploads
							// now. Each batch is checked once, as a whole. There's no
							// `accept`: it would drop dragged files of other types without
							// a word, where the check says why they can't be attached.
							beforeUpload={(file, batch) => {
								if (file === batch[0]) addFiles(batch);
								return Upload.LIST_IGNORE;
							}}
						>
							<p className="ant-upload-drag-icon">
								<InboxOutlined aria-hidden="true" />
							</p>
							<p className="ant-upload-text">
								Click or drag files to this area to attach them
							</p>
							<p className="ant-upload-hint">
								Up to {MAX_ATTACHMENTS} files and {MAX_ATTACHMENTS_MB} MB in
								total: PDF, Word, Excel, PowerPoint, text, CSV, PNG, JPEG, EML
								or MSG.
							</p>
						</Upload.Dragger>
						<div className="help-contact-form__error" aria-live="polite">
							{attachmentError && (
								<Alert type="error" showIcon message={attachmentError} />
							)}
						</div>
						{files.length > 0 && (
							<>
								<ul className="help-contact-form__files">
									{files.map((file) => (
										<li key={file.uid}>
											<PaperClipOutlined aria-hidden="true" />
											<span className="help-contact-form__file-name">
												{file.name}
											</span>
											<span className="help-contact-form__file-size">
												{fileTools.formatFileSize(file.size)}
											</span>
											<Button
												type="text"
												size="small"
												icon={<CloseOutlined aria-hidden="true" />}
												aria-label={`Remove ${file.name}`}
												disabled={isSending}
												onClick={() =>
													setFiles((current) =>
														current.filter((item) => item.uid !== file.uid)
													)
												}
											/>
										</li>
									))}
								</ul>
								<p className="help-contact-form__total">
									{files.length} of {MAX_ATTACHMENTS} files,{" "}
									{fileTools.formatFileSize(totalBytes(files))} of{" "}
									{MAX_ATTACHMENTS_MB} MB
								</p>
							</>
						)}
					</Form.Item>
					<Form.Item
						label="Preferred contact method"
						htmlFor={`${ids}-method`}
						extra={
							phone
								? undefined
								: "Add a phone number in My profile to be contacted by phone."
						}
					>
						<Select
							id={`${ids}-method`}
							value={contactMethod}
							onChange={setContactMethod}
							options={[
								{ value: "email", label: "Email" },
								{ value: "phone", label: "Phone", disabled: !phone },
							]}
						/>
					</Form.Item>
					<Form.Item>
						<Checkbox
							checked={consent}
							onChange={(event) => setConsent(event.target.checked)}
						>
							{CONSENT_TEXT}
						</Checkbox>
					</Form.Item>
					{!currentTenantKey && (
						<Alert
							type="warning"
							showIcon
							message="Choose a tenant before sending a request."
						/>
					)}
					{sendError && <Alert type="error" showIcon message={sendError} />}
				</Form>
			)}
		</Drawer>
	);
}

export default ContactSupportDrawer;
